/**
 * 响应式适配工具
 * 设计稿基准: 1920x1080
 */

// 设计稿基准尺寸
export const DESIGN_WIDTH = 1920;
export const DESIGN_HEIGHT = 1080;

/**
 * 获取当前屏幕缩放比例
 */
export const getScale = () => {
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  // 计算宽高比例
  const scaleX = windowWidth / DESIGN_WIDTH;
  const scaleY = windowHeight / DESIGN_HEIGHT;

  // 取较小的比例以保证内容完全可见
  return Math.min(scaleX, scaleY);
};

/**
 * 将设计稿尺寸转换为实际尺寸（px -> 适配后的px）
 * @param designPx 设计稿中的像素值
 * @returns 适配后的像素值（带单位）
 */
export const px = (designPx: number): string => {
  const scale = getScale();
  return `${designPx * scale}px`;
};

/**
 * 将设计稿尺寸转换为vw单位
 * @param designPx 设计稿中的像素值
 * @returns vw值（带单位）
 */
export const vw = (designPx: number): string => {
  return `${(designPx / DESIGN_WIDTH) * 100}vw`;
};

/**
 * 将设计稿尺寸转换为vh单位
 * @param designPx 设计稿中的像素值
 * @returns vh值（带单位）
 */
export const vh = (designPx: number): string => {
  return `${(designPx / DESIGN_HEIGHT) * 100}vh`;
};

/**
 * 监听窗口大小变化
 * @param callback 回调函数
 * @returns 取消监听的函数
 */
export const onResize = (callback: () => void): (() => void) => {
  window.addEventListener('resize', callback);
  return () => window.removeEventListener('resize', callback);
};

/**
 * 获取响应式字体大小
 * @param baseFontSize 基准字体大小（px）
 * @returns 响应式字体大小（带单位）
 */
export const fontSize = (baseFontSize: number): string => {
  return vw(baseFontSize);
};

/**
 * 获取响应式行高
 * @param baseLineHeight 基准行高（px）
 * @returns 响应式行高（带单位）
 */
export const lineHeight = (baseLineHeight: number): string => {
  return vw(baseLineHeight);
};

/**
 * 获取响应式间距
 * @param baseSpacing 基准间距（px）
 * @returns 响应式间距（带单位）
 */
export const spacing = (baseSpacing: number): string => {
  return vw(baseSpacing);
};

/**
 * 设置根元素字体大小（用于rem单位）
 */
export const setRootFontSize = () => {
  const scale = getScale();
  const rootFontSize = 16 * scale; // 基准字体16px
  document.documentElement.style.fontSize = `${rootFontSize}px`;
};

/**
 * 初始化响应式适配
 */
export const initResponsive = () => {
  // 设置根元素字体大小
  setRootFontSize();

  // 监听窗口大小变化
  onResize(() => {
    setRootFontSize();
  });

  console.log('[Responsive] 响应式适配已初始化');
  console.log(`[Responsive] 设计稿尺寸: ${DESIGN_WIDTH}x${DESIGN_HEIGHT}`);
  console.log(`[Responsive] 当前屏幕: ${window.innerWidth}x${window.innerHeight}`);
  console.log(`[Responsive] 缩放比例: ${getScale().toFixed(3)}`);
};
